import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { userService } from '../../services/api';
import { format } from 'date-fns';

const UserDetail = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await userService.getById(id);
        setUser(response.data.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">User not found</h2>
        <Link to="/users" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
          Back to users
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/users" className="hover:text-gray-700">Users</Link>
        <span>/</span>
        <span>{user.firstName} {user.lastName}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-body text-center">
              <div className="h-20 w-20 rounded-full bg-primary-600 flex items-center justify-center text-white text-2xl font-bold mx-auto">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-gray-500">{user.email}</p>
              <span className={`badge mt-2 ${user.isActive ? 'badge-green' : 'badge-red'}`}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="card-header">
              <h3 className="font-medium">User Details</h3>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">Role</dt>
                  <dd className="font-medium">{user.role}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Employee ID</dt>
                  <dd className="font-medium">{user.employeeId || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Phone</dt>
                  <dd className="font-medium">{user.phone || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Last Login</dt>
                  <dd className="font-medium">
                    {user.lastLogin ? format(new Date(user.lastLogin), 'MMM d, yyyy h:mm a') : 'Never'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Created</dt>
                  <dd className="font-medium">
                    {format(new Date(user.createdAt), 'MMM d, yyyy')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Verified</dt>
                  <dd className="font-medium">{user.isVerified ? 'Yes' : 'No'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Hotels */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-medium">Assigned Hotels</h3>
            </div>
            <div className="card-body">
              {user.hotels?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.hotels.map((hotel) => (
                    <span key={hotel.id} className="badge badge-blue">
                      {hotel.name}
                      {hotel.is_primary && ' (Primary)'}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No hotels assigned</p>
              )}
            </div>
          </div>

          {/* Teams */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-medium">Assigned Teams</h3>
            </div>
            <div className="card-body">
              {user.teams?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.teams.map((team) => (
                    <span key={team.id} className="badge badge-green">
                      {team.name}
                      {team.is_lead && ' (Lead)'}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No teams assigned</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;
